/**
 * Meal Plan Types
 *
 * TypeScript interfaces for meal plan management in the BiteWise healthcare provider platform.
 * Based on the BiteWise API documentation for meal plan endpoints.
 *
 * @module types/mealPlan
 */

/**
 * Meal types supported by the system
 */
export type MealType = 'breakfast' | 'morning_tea' | 'lunch' | 'afternoon_tea' | 'dinner' | 'dessert';

/**
 * Meal plan assignment status
 */
export type MealPlanAssignmentStatus = 'active' | 'completed' | 'archived';

/**
 * Ingredient information for a meal (API format)
 */
export interface MealIngredient {
  name: string;
  amount: number;
  unit: string;
  cost_aud: number;
}

/**
 * Nutrition information for a meal (API format)
 */
export interface MealNutrition {
  calories_per_serving: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g: number;
  sugar_g: number;
  sodium_mg: number;
  calories_per_100g: number;
}

/**
 * Individual meal within a meal plan (API format)
 */
export interface Meal {
  id: string;
  meal_name: string;
  meal_type: MealType;
  cuisine: string;
  prep_time_minutes: number;
  cook_time_minutes: number;
  servings: number;
  difficulty: string;
  ingredients: MealIngredient[];
  instructions: string[];
  nutrition: MealNutrition;
  tags: string[];
  estimated_cost_aud: number;
}

/**
 * Complete meal plan data structure (API format)
 */
export interface MealPlanData {
  meals: Meal[];
  total_estimated_cost_aud: number;
  plan_duration_days: number;
  generated_at: string;
}

/**
 * Performance tracking data from MealPlan SDK
 */
export interface MealPlanPerformanceData {
  total_generation_time_ms: number;
  total_tokens_used: number;
  total_tokens_prompt: number;
  total_tokens_completion: number;
  total_api_calls: number;
  successful_calls: number;
  failed_calls: number;
  retry_attempts: number;
  circuit_breaker_trips: number;
  rate_limit_blocks: number;
  parallel_meals_generated: number;
  meals_requiring_refinement: number;
  avg_meal_generation_time_ms: number;
  evaluation_method: string;
  evaluation_failures: number;
  evaluation_success_rate: number;
  concurrent_evaluations: number;
  concept_generation_enabled: boolean;
  concept_generation_time_ms: number;
  concepts_generated: number;
  concept_generation_failures: number;
  diversity_score: number;
  final_diversity_score: number;
  cooking_method_distribution: Record<string, number>;
  cuisine_distribution: Record<string, number>;
  diversity_constraints_met: boolean;
  unique_cooking_methods: number;
  unique_cuisines: number;
  prep_time_category: string;
  prep_time_compliance_rate: number;
  prep_time_refinements: number;
  avg_time_reduction_minutes: number;
  api_call_details: Array<{
    operation: string;
    start_time: string;
    end_time: string;
    duration_ms: number;
    tokens_used: number | null;
    tokens_prompt: number | null;
    tokens_completion: number | null;
    model: string;
    temperature: number;
    success: boolean;
    error_message: string | null;
    retry_count: number;
    meal_type: string | null;
    meal_index: number | null;
  }>;
  operation_summary: Record<string, {
    count: number;
    success_count: number;
    total_duration_ms: number;
    total_tokens: number;
    avg_duration_ms: number;
    success_rate: number;
  }>;
}

/**
 * Parameters used to create a meal plan
 */
export interface MealPlanCreationParameters {
  meal_count: number;
  meal_types: MealType[];
  dietary_restrictions?: string[];
  allergies?: string[];
  disliked_ingredients?: string[];
  cuisine_preferences?: string[];
  budget_aud?: number;
  servings_per_meal?: number;
  max_prep_time?: number;
  health_conditions?: string[];
}

/**
 * Assignment information when meal plan is assigned to patients
 */
export interface MealPlanAssignmentInfo {
  id: string;
  patient_id: string;
  patient_name: string;
  assigned_by_id: string;
  assigned_by_name: string;
  assigned_at: string;
  notes?: string;
  status: MealPlanAssignmentStatus;
}

/**
 * Complete meal plan response from API
 */
export interface MealPlan {
  id: string;
  creator_id: string;
  creator_name: string;
  label?: string;
  total_estimated_cost_aud: number;
  plan_duration_days: number;
  meal_count: number;
  creation_parameters: MealPlanCreationParameters;
  meal_plan_data: MealPlanData;
  performance_data: MealPlanPerformanceData;
  created_at: string;
  updated_at?: string;
  generation_time_ms: number;
  sdk_version: string;
  total_token_usage: number;
  assignment_info?: MealPlanAssignmentInfo;
}

/**
 * Request to create a new meal plan
 */
export interface CreateMealPlanRequest {
  meal_count: number;
  meal_types: MealType[];
  dietary_restrictions?: string[];
  allergies?: string[];
  disliked_ingredients?: string[];
  cuisine_preferences?: string[];
  budget_aud?: number;
  servings_per_meal?: number;
  label?: string;
  max_prep_time?: number;
  health_conditions?: string[];
  user_guidance?: string;
}

/**
 * Paginated response for meal plans list
 */
export interface MealPlansListResponse {
  meal_plans: MealPlan[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

/**
 * Request to assign meal plan to patient
 */
export interface AssignMealPlanRequest {
  meal_plan_id: string;
  patient_id: string;
  notes?: string;
}

/**
 * Response when assigning meal plan to patient
 */
export interface AssignMealPlanResponse {
  id: string;
  meal_plan_id: string;
  patient_id: string;
  patient_name: string;
  assigned_by_id: string;
  assigned_by_name: string;
  assigned_at: string;
  notes?: string;
  status: MealPlanAssignmentStatus;
  meal_plan: MealPlan;
}

/**
 * Request to update assignment status
 */
export interface UpdateAssignmentStatusRequest {
  status: MealPlanAssignmentStatus;
}

/**
 * Parameters for getting meal plans
 */
export interface GetMealPlansParams {
  page?: number;
  per_page?: number;
}

/**
 * Parameters for getting patient meal plans
 */
export interface GetPatientMealPlansParams {
  status?: MealPlanAssignmentStatus;
}

/**
 * Ingredient information for a meal
 */
export interface MealIngredientFrontend {
  name: string;
  amount: number;
  unit: string;
  costAud: number;
}

/**
 * Detailed nutrition information for a meal
 */
export interface MealNutritionFrontend {
  caloriesPerServing: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  fiberG: number;
  sugarG: number;
  sodiumMg: number;
  caloriesPer100g: number;
}

/**
 * Detailed meal information
 */
export interface MealFrontend {
  id: string;
  mealName: string;
  mealType: MealType;
  cuisine: string;
  dayNumber?: number;
  dayLabel?: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  servings: number;
  difficulty: string;
  ingredients: MealIngredientFrontend[];
  instructions: string[];
  nutrition: MealNutritionFrontend;
  tags: string[];
  estimatedCostAud: number;
}

/**
 * Comprehensive performance data from meal plan generation
 */
export interface MealPlanPerformanceDataFrontend {
  totalGenerationTimeMs: number;
  totalTokensUsed: number;
  totalTokensPrompt: number;
  totalTokensCompletion: number;
  totalApiCalls: number;
  successfulCalls: number;
  failedCalls: number;
  retryAttempts: number;
  circuitBreakerTrips: number;
  rateLimitBlocks: number;
  parallelMealsGenerated: number;
  mealsRequiringRefinement: number;
  avgMealGenerationTimeMs: number;
  evaluationMethod: string;
  evaluationFailures: number;
  evaluationSuccessRate: number;
  concurrentEvaluations: number;
  conceptGenerationEnabled: boolean;
  conceptGenerationTimeMs: number;
  conceptsGenerated: number;
  conceptGenerationFailures: number;
  diversityScore: number;
  finalDiversityScore: number;
  cookingMethodDistribution: Record<string, number>;
  cuisineDistribution: Record<string, number>;
  diversityConstraintsMet: boolean;
  uniqueCookingMethods: number;
  uniqueCuisines: number;
  prepTimeCategory: string;
  prepTimeComplianceRate: number;
  prepTimeRefinements: number;
  avgTimeReductionMinutes: number;
  apiCallDetails: Array<{
    operation: string;
    startTime: string;
    endTime: string;
    durationMs: number;
    tokensUsed: number | null;
    tokensPrompt: number | null;
    tokensCompletion: number | null;
    model: string;
    temperature: number;
    success: boolean;
    errorMessage: string | null;
    retryCount: number;
    mealType: string | null;
    mealIndex: number | null;
  }>;
  operationSummary: Record<string, {
    count: number;
    successCount: number;
    totalDurationMs: number;
    totalTokens: number;
    avgDurationMs: number;
    successRate: number;
  }>;
}

/**
 * Frontend-friendly meal plan interface (camelCase)
 */
export interface MealPlanFrontend {
  id: string;
  creatorId: string;
  creatorName: string;
  creatorPhotoUrl?: string | null;
  label?: string;
  totalEstimatedCostAud: number;
  planDurationDays: number;
  mealCount: number;
  creationParameters: {
    mealCount: number;
    mealTypes: MealType[];
    dietaryRestrictions?: string[];
    allergies?: string[];
    dislikedIngredients?: string[];
    cuisinePreferences?: string[];
    budgetAud?: number;
    servingsPerMeal?: number;
    label?: string;
    maxPrepTime?: number;
    healthConditions?: string[];
  };
  mealPlanData: {
    meals: MealFrontend[];
    totalEstimatedCostAud: number;
    planDurationDays: number;
    generatedAt: string;
  };
  performanceData: MealPlanPerformanceDataFrontend;
  createdAt: string;
  updatedAt?: string;
  generationTimeMs: number;
  sdkVersion: string | null;
  totalTokenUsage: number;
  assignmentInfo?: {
    id: string;
    patientId: string;
    patientName: string;
    assignedById: string;
    assignedByName: string;
    assignedAt: string;
    notes?: string;
    status: MealPlanAssignmentStatus;
  };
}

/**
 * Frontend-friendly create meal plan request (camelCase)
 */
export interface CreateMealPlanRequestFrontend {
  mealCount?: number;
  mealTypes: MealType[];
  dietaryRestrictions?: string[];
  religiousRestrictions?: string[];
  allergies?: string[];
  dislikedIngredients?: string[];
  cuisinePreferences?: string[];
  budgetAud?: number;
  servingsPerMeal?: number;
  label?: string;
  maxPrepTime?: number;
  healthConditions?: string[];
  userGuidance?: string;
}

/**
 * Frontend-friendly assign meal plan request (camelCase)
 */
export interface AssignMealPlanRequestFrontend {
  mealPlanId: string;
  patientId: string;
  notes?: string;
}

/**
 * Frontend-friendly assignment response (camelCase)
 */
export interface AssignMealPlanResponseFrontend {
  id: string;
  mealPlanId: string;
  patientId: string;
  patientName: string;
  assignedById: string;
  assignedByName: string;
  assignedAt: string;
  notes?: string;
  status: MealPlanAssignmentStatus;
  mealPlan: MealPlanFrontend;
}

/**
 * Available actions for meal editing
 */
export type MealAction = 'edit' | 'adjust' | 'swap';

/**
 * Regeneration mode for AI meal adjustments
 */
export type RegenerationMode = 'fast' | 'quality';

/**
 * Form data for editing a meal
 * All fields are required as per API specification
 */
export interface EditMealFormData {
  /** Updated meal name (1-200 characters) */
  mealName: string;
  /** Updated nutrition information */
  nutrition: {
    /** Calories per serving (0-5000) */
    caloriesPerServing: number;
    /** Protein in grams (0-500) */
    proteinG: number;
    /** Carbohydrates in grams (0-500) */
    carbsG: number;
    /** Fat in grams (0-200) */
    fatG: number;
    /** Fiber in grams (0-100) - optional */
    fiberG?: number;
    /** Sugar in grams (0-200) - optional */
    sugarG?: number;
    /** Sodium in milligrams (0-5000) - optional */
    sodiumMg?: number;
  };
}

/**
 * Validation errors for edit meal form
 */
export interface EditMealFormErrors {
  mealName?: string;
  calories?: string;
  protein?: string;
  carbs?: string;
  fat?: string;
  fiber?: string;
  sugar?: string;
  sodium?: string;
}

/**
 * Form data for adjusting (regenerating) a meal
 */
export interface AdjustMealFormData {
  /** Guidance for AI refinement (1-140 characters) */
  guidance: string;
}

/**
 * Validation errors for adjust meal form
 */
export interface AdjustMealFormErrors {
  guidance?: string;
}

/**
 * Form data for swapping a meal
 */
export interface SwapMealFormData {
  /** Special instructions for new meal generation (10-140 characters) */
  specialInstructions: string;
  /** Skip evaluation for faster generation (default: true) */
  skipEvaluation?: boolean;
}

/**
 * Validation errors for swap meal form
 */
export interface SwapMealFormErrors {
  specialInstructions?: string;
}

/**
 * Request payload for PUT /api/mealplan/{meal_plan_id}/meals/{meal_id}
 * Snake_case format for API compatibility
 */
export interface UpdateMealRequest {
  meal_name: string;
  nutrition: {
    calories_per_serving: number;
    protein_g: number;
    carbs_g: number;
    fat_g: number;
    fiber_g?: number;
    sugar_g?: number;
    sodium_mg?: number;
  };
}

/**
 * Response from update meal API
 * Contains the updated meal object
 */
export interface UpdateMealResponse {
  meal: Meal & { id: string };
}

/**
 * Request payload for POST /api/mealplan/{meal_plan_id}/meals/{meal_id}/regenerate
 * Snake_case format for API compatibility
 */
export interface RegenerateMealRequest {
  guidance: string;
}

/**
 * Response from regenerate meal API
 * Contains the regenerated meal object
 */
export interface RegenerateMealResponse {
  meal: Meal & { id: string };
}

/**
 * Request payload for POST /api/mealplan/{meal_plan_id}/meals/{meal_id}/swap
 * Snake_case format for API compatibility
 */
export interface SwapMealRequest {
  guidance: string;
  additional_preferences?: {
    cuisine_preferences?: string[];
    servings_per_meal?: number;
  };
  skip_evaluation?: boolean;
}

/**
 * Response from swap meal API
 * Contains the complete updated meal plan with swapped meal
 */
export interface SwapMealResponse {
  meal_plan: MealPlan;
  swapped_meal: Meal & { id: string };
}

/**
 * Maximum character limits for text inputs
 */
export const MAX_LENGTHS = {
  MEAL_NAME: 200,
  GUIDANCE: 140,
  SPECIAL_INSTRUCTIONS: 140,
} as const;

/**
 * Minimum character limits for text inputs
 */
export const MIN_LENGTHS = {
  MEAL_NAME: 1,
  GUIDANCE: 1,
  SPECIAL_INSTRUCTIONS: 10,
} as const;

/**
 * Numeric value ranges
 */
export const VALUE_RANGES = {
  CALORIES: { min: 0, max: 5000 },
  PROTEIN: { min: 0, max: 500 },
  CARBS: { min: 0, max: 500 },
  FAT: { min: 0, max: 200 },
  FIBER: { min: 0, max: 100 },
  SUGAR: { min: 0, max: 200 },
  SODIUM: { min: 0, max: 5000 },
} as const;

/**
 * Default form values
 */
export const DEFAULT_FORM_VALUES = {
  SKIP_EVALUATION: true,
} as const;